// ============================================================================
// Standalone employer eligibility check — /employer-check/:apikey
// ============================================================================
// This is the surface the CANDIDATE forwards to their EMPLOYER. The employer is
// a different person from the lead: they have no conversation, no chat history
// and no reason to install anything, so this page stands on its own.
//
// `?ref=<conversationId>` ties the employer's result back to the candidate's
// case, so the adviser sees both halves of the same nomination in one place.
// Without it the check still works — it just files as an unattributed lead.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import OINPEmployerChecker from './OINPEmployerChecker';
import OINPEmployerResults from './OINPEmployerResults';
import { saveAssessment, requestEmployerChecklist, anonymousEntryContext } from '../services/assessments';
import { translator } from '../assessments/oinp/i18n';

const Page = styled.div`
  min-height: 100vh;
  padding: 24px 16px 48px;
  background: #fdf9f0;
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
`;

const Shell = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const Masthead = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.25;
  color: #1a2332;
`;

const Sub = styled.p`
  margin: 0;
  font-size: 13.5px;
  line-height: 1.6;
  color: #6b5a42;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const LegalFooter = styled.p`
  margin: 20px auto 0;
  max-width: 640px;
  font-size: 10.5px;
  line-height: 1.6;
  text-align: center;
  color: #94a3b8;
`;

const EmployerCheckPage = () => {
  const { apikey } = useParams();
  const [searchParams] = useSearchParams();
  const referringConversationId = searchParams.get('ref');
  const language = searchParams.get('lang') || 'en';
  const scheme = searchParams.get('scheme') || 'blue';

  const tr = translator(language);
  const [result, setResult] = useState(null);
  // Discipline gate. This page is public and reachable by URL alone, so it must
  // confirm the adviser behind the api key actually offers the employer check
  // rather than trusting the link. 'checking' until the server answers — the
  // questionnaire must never flash up for an adviser who doesn't offer it.
  const [access, setAccess] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    anonymousEntryContext(apikey).then(ctx => {
      if (cancelled) return;
      const allowed = (ctx?.widgetConfig?.assessmentTemplates || []).includes('oinp-employer');
      setAccess(allowed ? 'allowed' : 'denied');
    });
    return () => { cancelled = true; };
  }, [apikey]);

  const handleComplete = (employerResult, answers) => {
    setResult(employerResult);
    saveAssessment({
      type: 'oinp_employer',
      answers,
      result: employerResult,
      language,
      apikey,
      conversationId: referringConversationId,
      source: 'standalone-employer-page',
    }).then(stored => { if (stored?.result) setResult(stored.result); });
  };

  const handleChecklist = (email, employerResult) => {
    requestEmployerChecklist({
      email,
      result: employerResult,
      language,
      apikey,
      conversationId: referringConversationId,
      // Link back to this same page so the employer can re-read and print it.
      checkerLink: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  };

  return (
    <Page>
      <Shell>
        <Masthead>
          <Title>{tr('employerTitle')}</Title>
          <Sub>{tr('employerSubtitle')}</Sub>
        </Masthead>

        <Panel>
          {access === 'checking' ? (
            <Sub>{tr('loading')}</Sub>
          ) : access === 'denied' ? (
            <Sub>{tr('employerUnavailable')}</Sub>
          ) : result ? (
            <OINPEmployerResults
              result={result}
              onRequestChecklist={handleChecklist}
              language={language}
              scheme={scheme}
            />
          ) : (
            <OINPEmployerChecker
              onComplete={handleComplete}
              language={language}
              scheme={scheme}
            />
          )}
        </Panel>
      </Shell>

      <LegalFooter>{tr('disclaimer')}</LegalFooter>
    </Page>
  );
};

export default EmployerCheckPage;
